import useIsMobile from "@common/hooks/useIsMobile"
import { ReactNode, useState, useEffect } from "react"
import { Transition } from "@headlessui/react"
import Svg from "@common/svg"
import { joinClassNames } from "@common/lib/util"

type SidePanelLayoutProps = {
  children: ReactNode
}

type MainProps = {
  children: ReactNode
}

type SideProps = {
  show?: boolean
  children: ReactNode
  isCollapsible?: boolean
  align?: "left" | "right"
  onChange?: (isOpen: boolean) => void
  widthClass?: string
}

const SidePanelLayout = ({ children }: SidePanelLayoutProps) => {
  return <div className="flex h-full">{children}</div>
}

export const Main = ({ children }: MainProps) => {
  return <div className="flex-grow overflow-auto">{children}</div>
}

export const Side = ({
  show = false,
  children,
  isCollapsible = true,
  align = "right",
  onChange,
  widthClass = "w-full",
}: SideProps) => {
  const isMobile = useIsMobile()
  const [isOpen, setIsOpen] = useState(isMobile ? false : show)

  useEffect(() => {
    setIsOpen(isMobile ? false : show)
  }, [show, isMobile])

  const togglePanel = () => {
    onChange?.(!isOpen)
    setIsOpen(!isOpen)
  }

  const sideClassName = joinClassNames(
    "relative z-50 h-full inset-y-0",
    align === "left" ? "left-0" : "right-0"
  )
  const panelClassName = joinClassNames(
    "h-full flex flex-col relative overflow-auto print:hidden",
    align === "left" ? "border-r" : "border-l",
    isMobile ? "w-screen" : widthClass
  )

  let buttonClassName = ""
  if (isOpen)
    buttonClassName = align === "left" ? "pullButtonRight" : "pullButtonLeft"
  else buttonClassName = align === "left" ? "pullButtonLeft" : "pullButtonRight"

  const iconClassName = joinClassNames(
    "h-6 w-6 transform ease-in-out duration-100 text-gray-800 dark:text-gray-500",
    align === "left"
      ? isOpen === false
        ? " rotate-0 "
        : " rotate-180"
      : isOpen === false
        ? " rotate-180 "
        : " rotate-0"
  )

  return (
    <div className={sideClassName}>
      <Transition
        show={isOpen}
        enter="transition ease-in-out duration-100 transform"
        enterFrom={align === "left" ? "-translate-x-full" : "translate-x-full"}
        enterTo="translate-x-0"
        leave="transition ease-in-out duration-100 transform"
        leaveFrom="translate-x-0"
        leaveTo={align === "left" ? "-translate-x-full" : "translate-x-full"}
      >
        <div className={panelClassName}>{children}</div>
      </Transition>
      {isCollapsible && (
        <button onClick={togglePanel} className={buttonClassName}>
          <Svg
            name="arrow-right-short"
            classes={iconClassName}
            stroke="currentColor"
          />
        </button>
      )}
    </div>
  )
}

SidePanelLayout.Main = Main
SidePanelLayout.Side = Side

export default SidePanelLayout
